import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { LoadingSpinner } from "@/src/skeletons/LoadingSpinner";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { Link, LogInIcon, Plus, UserPlus, UserPlus2 } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { FiUserPlus } from "react-icons/fi";
import { FaCheck, FaGithub, FaSpinner } from "react-icons/fa6";
import { FaFacebookF } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { completeLogin, login, SociaLogin } from "../actions/authAction";
import { useAuth } from "../provider/AuthProvider";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import ForgotPassword from "../components/ForgotPassword";
import Register from "./user/Register";

const USER_REGEX = /^[A-z][A-z0-9-_]{3,23}$/;
const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%]).{8,24}$/;
const REGISTER_URL = "/register";
const formSchema = z.object({
  username: z
    .string({ required_error: "User email is required" })
    .min(1, { message: "User email cannot be empty" })
    .email("This is not a valid username"),
  password: z
    .string({ required_error: "Password is required" })
    .min(1, "Password is required"),
});
const Login = ({ className, fromLogin, handleClose }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const returnUrl = params.get("returnUrl");
  const token = params.get("token");

  const { state } = useLocation();
  const { setToken } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userName, setUserName] = useState('')
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: userName,
      password: "",
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [openForgotPassword, setOpenForgotPassword] = useState(false);

  useEffect(() => {
    if (token) {
      setToken(token);
      navigate(returnUrl || '/', { replace: true });
    }
  }, [token])
  async function onSubmit(obj) {
    setIsLoading(true);
    setShowError(false);
    obj.includeProfile = true;
    dispatch(
      login(obj, (resp, token) => {
        if (resp) {
          setToken(token);
          if (fromLogin) {
            handleClose(token);
          } else {
            if (state && state.from && state.from == "listBook") {
              navigate("/flights");
              return;
            } else {
              returnUrl == null ? navigate('/') :
                navigate(returnUrl, { replace: true });
            }
          }
        } else {
          setShowError(true);
        }
        setIsLoading(false);
      })
    );
  }
  const handleSocialLogin = (provider) => {
    const deviceInfo = "android"
    const deviceType = "mobile"
    // Navigate browser directly to your server endpoint that issues the redirect
    const url = `/TravelApi/ExternalLogin?provider=${provider}&deviceInfo=${deviceInfo}&deviceType=${deviceType}`;
    window.location.href = url;
  };
  return (
    <>
      {showRegisterForm ? (
        <Register
          handleClose={() => {
            handleClose();
          }}
          fromRegister
        />
      ) : (
        <div className="p-2 lg:p-8">
          <div
            className={
              !fromLogin
                ? "bg-background mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[480px] border shadow p-5 rounded"
                : ""
            }
          >
            <div className="flex flex-col space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">Login</h1>
              <p className="text-sm text-muted-foreground">
                Enter your email and password
              </p>
            </div>
            <div className={cn("grid gap-6", className)}>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <div className="grid gap-2">
                    <FormField
                      control={form.control}
                      name="username"
                      render={({ field }) => (
                        <div className="grid gap-1">
                          <FormItem>
                            <Label className="sr-only" htmlFor="username">
                              User Name
                            </Label>
                            <Input
                              id="username"
                              type="username"
                              placeholder="Email"
                              onChange={field.onChange}
                              disabled={isLoading}
                            />
                            <FormMessage />
                          </FormItem>
                        </div>
                      )}
                    ></FormField>

                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <div className="grid gap-1">
                          <FormItem>
                            <Label className="sr-only" htmlFor="password">
                              Password
                            </Label>
                            <Input
                              id="password"
                              type="password"
                              placeholder="Password"
                              onChange={field.onChange}
                              disabled={isLoading}
                            />
                            <FormMessage />
                          </FormItem>
                        </div>
                      )}
                    ></FormField>
                    <Button disabled={isLoading}>
                      {isLoading && (
                        <FaSpinner className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      <LogInIcon className="mr-2 h-4 w-4" /> Login
                    </Button>
                  </div>
                  {showError && (
                    <div className="justify-center flex">
                      <FormMessage className="pt-2">
                        Username and password invalid.
                      </FormMessage>
                    </div>
                  )}
                  <div className="justify-center flex">
                    <Dialog open={openForgotPassword} onOpenChange={setOpenForgotPassword}>
                      <DialogTrigger asChild>
                        <Button type="button" variant="link" >
                          Forgot Password?
                        </Button>
                      </DialogTrigger>
                      {openForgotPassword && <ForgotPassword username={form.getValues('username')} successCallback={() => setOpenForgotPassword(false)} />}
                    </Dialog>
                  </div>
                </form>
              </Form>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-sm ">
                  <span className="bg-background px-2 text-muted-foreground">
                    Don't have account yet?
                  </span>
                </div>
              </div>
              {/* Social login buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => handleSocialLogin("google")}
                  disabled={isLoading}
                >
                  <FcGoogle className="mr-2 h-4 w-4" />
                  <span>Continue with Google</span>
                </Button>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => handleSocialLogin("facebook")}
                  disabled={isLoading}
                >
                  <FaFacebookF className="mr-2 h-4 w-4 text-blue-600" />
                  <span>Continue with Facebook</span>
                </Button>
              </div>
              <Button
                variant="outline"
                type="button"
                disabled={isLoading}
                onClick={() => {
                  fromLogin
                    ? setShowRegisterForm(true)
                    : navigate("/auth/register");
                }}
              >
                <FiUserPlus className="me-2 h-4 w-4 " />
                <span>Sign Up</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Login;
